import { getApolloClient } from 'lib/apollo-client';

import { updateUserAvatar } from 'lib/users';
import { sortObjectsByDate } from 'lib/datetime';

import {
  QUERY_ALL_POSTS,
  QUERY_POST_BY_SLUG,
  QUERY_POSTS_BY_AUTHOR_SLUG,
  QUERY_POSTS_BY_CATEGORY_ID,
  QUERY_POST_SEO_BY_SLUG,
  QUERY_POST_PER_PAGE,
  QUERY_POSTS_CATEGORY_ID_BY_YEAR,
  QUERY_POSTS_CATEGORY_ID_BY_KW,
  QUERY_POSTS_CATEGORY_ID_BY_YEAR_KW,
  QUERY_POSTS_YEARS_BY_CATEGORY_ID,
} from 'data/posts';

import { LANG_DEFAULT } from 'data/lang';
import { getAllPostsByCategorySlug, getDraftPostById, getPostsYearByCategorySlug } from 'data/fetchPlugin';
import moment from 'moment';
import _uniq from 'lodash/uniq';
import cache from "memory-cache";

/**
 * postPathBySlug
 */

export function postPathBySlug(slug) {
  return `/posts/${slug}`;
}

export function customPostTypePathBySlug(postType, slug) {
  return `/${postType}/${slug}`;
}

/**
 * getPostBySlug
 */

export async function getPostBySlug(slug, locale = LANG_DEFAULT) {
  const apolloClient = getApolloClient();
  const apiHost = new URL(process.env.WORDPRESS_GRAPHQL_ENDPOINT).host;

  let postData;
  let seoData;
  let lang = locale.toUpperCase()

  try {
    postData = await apolloClient.query({
      query: QUERY_POST_BY_SLUG,
      variables: {
        slug,
        lang
      },
      fetchPolicy: `no-cache`
    });
  } catch (e) {
    console.log(`[posts][getPostBySlug] Failed to query post data: ${e.message}`);
    throw e;
  }
  if (!postData?.data?.post?.translation) return { post: {} }
  const post = [postData?.data?.post?.translation].map(mapPostData)[0];

  // If the SEO plugin is enabled, look up the data
  // and apply it to the default settings

  if (process.env.WORDPRESS_PLUGIN_SEO === true) {
    try {
      seoData = await apolloClient.query({
        query: QUERY_POST_SEO_BY_SLUG,
        variables: {
          slug,
          lang
        },
        // fetchPolicy: process.env.NODE_ENV === 'development' ? "no-cache" : "cache-first"
      });
    } catch (e) {
      console.log(`[posts][getPostBySlug] Failed to query SEO plugin: ${e.message}`);
      console.log('Is the SEO Plugin installed? If not, disable WORDPRESS_PLUGIN_SEO in next.config.js.');
      throw e;
    }

    const { seo = {} } = seoData?.data?.post?.translation;

    post.metaTitle = seo.title;
    post.metaDescription = seo.metaDesc;
    post.readingTime = seo.readingTime;

    // The SEO plugin by default includes a canonical link, but we don't want to use that
    // because it includes the WordPress host, not the site host. We manage the canonical
    // link along with the other metadata, but explicitly check if there's a custom one
    // in here by looking for the API's host in the provided canonical link

    if (seo.canonical && !seo.canonical.includes(apiHost)) {
      post.canonical = seo.canonical;
    }

    post.og = {
      author: seo.opengraphAuthor,
      description: seo.opengraphDescription,
      image: seo.opengraphImage,
      modifiedTime: seo.opengraphModifiedTime,
      publishedTime: seo.opengraphPublishedTime,
      publisher: seo.opengraphPublisher,
      title: seo.opengraphTitle,
      type: seo.opengraphType,
    };

    post.article = {
      author: post.og.author,
      modifiedTime: post.og.modifiedTime,
      publishedTime: post.og.publishedTime,
      publisher: post.og.publisher,
    };

    post.robots = {
      nofollow: seo.metaRobotsNofollow,
      noindex: seo.metaRobotsNoindex,
    };

    post.twitter = {
      description: seo.twitterDescription,
      image: seo.twitterImage,
      title: seo.twitterTitle,
    };
  }

  return {
    post,
  };
}

export async function getPostBySlugV2(slug, locale = LANG_DEFAULT) {
  const apiHost = new URL(process.env.WORDPRESS_API_ENDPOINT).host;

  let postData;
  let seoData;
  const lang = locale.toLowerCase()

  try {
    const route = `/v1/post/_/detail&slug=${slug}&locale=${lang}`
    const url = encodeURI(`${process.env.WORDPRESS_API_ENDPOINT}?rest_route=${route}`);
    const res = await fetch(url);
    postData = await res.json();
  } catch (error) {
    console.error(`[pages][getPostBySlugV2] Failed to query: ${error}`);
    postData = null
  }
  const post = {
    ...postData,
    metaTitle: '',
    metaDescription: '',
    readingTime: 0,
    og: {},
    article: {},
    robots: {},
    twitter: {}
  }

  // If the SEO plugin is enabled, look up the data
  // and apply it to the default settings

  if (process.env.WORDPRESS_PLUGIN_SEO === true) {
    try {
      const route = `/v1/post/_/seo/${post.databaseId}`
      const url = encodeURI(`${process.env.WORDPRESS_API_ENDPOINT}?rest_route=${route}`);
      const res = await fetch(url);
      seoData = await res.json();
    } catch (error) {
      console.error('Failed to fetch seo post:', error);
    }
    const seo = seoData?.seo || {}
    post.metaTitle = seo.title;
    post.metaDescription = seo.metaDesc;
    post.readingTime = seo.readingTime;

    // The SEO plugin by default includes a canonical link, but we don't want to use that
    // because it includes the WordPress host, not the site host. We manage the canonical
    // link along with the other metadata, but explicitly check if there's a custom one
    // in here by looking for the API's host in the provided canonical link

    post.canonical = seo.canonical;

    post.og = {
      author: seo.opengraphAuthor,
      description: seo.opengraphDescription,
      image: seo.opengraphImage,
      modifiedTime: seo.opengraphModifiedTime,
      publishedTime: seo.opengraphPublishedTime,
      publisher: seo.opengraphPublisher,
      title: seo.opengraphTitle,
      type: seo.opengraphType,
    };

    post.article = {
      author: post.og.author,
      modifiedTime: post.og.modifiedTime,
      publishedTime: post.og.publishedTime,
      publisher: post.og.publisher,
    };

    post.robots = {
      nofollow: seo.metaRobotsNofollow,
      noindex: seo.metaRobotsNoindex,
    };

    post.twitter = {
      description: seo.twitterDescription,
      image: seo.twitterImage,
      title: seo.twitterTitle,
    };
  }

  return {
    post,
  };
}

/*
  * getDraftPostBy PostId
 */

export async function getDraftPostByPostId(postId) {
  let PostData;
  let error = false
  let code = 200
  try {
    PostData = await getDraftPostById(postId)
  } catch (e) {
    console.log(`[posts][getDraftPostByPostId] Failed to query post data: ${e.message}`);
    error = true
    code = 403
  }

  const post = [PostData].map(mapPostData)[0];

  return {
    post,
    error,
    code
  };
}

/**
 * getAllPosts
 */

export async function getAllPosts(locale = LANG_DEFAULT) {
  const apolloClient = getApolloClient();
  let lang = locale.toUpperCase()
  const data = await apolloClient.query({
    query: QUERY_ALL_POSTS,
    variables: {
      lang
    },
    // fetchPolicy: process.env.NODE_ENV === 'development' ? "no-cache" : "cache-first"
  });
  const posts = data?.data.posts.edges.map(({ node = {} }) => {
    return node
  });

  return {
    posts: Array.isArray(posts) && posts.map(mapPostData),
  };
}

export async function getAllPostsV2(locale) {

  let post_datas = []
  try {
    const route = `/v1/post/_/all&locale=${locale}`
    const url = encodeURI(`${process.env.WORDPRESS_API_ENDPOINT}?rest_route=${route}`);
    const res = await fetch(url);
    post_datas = await res.json();
  } catch (error) {
    console.error(`[pages][getAllPostsV2] Failed to query: ${error}`);
  }

  return {
    posts: Array.isArray(post_datas) && post_datas,
  };
}

/**
 * getPostsByAuthorSlug
 */

export async function getPostsByAuthorSlug(slug) {
  const apolloClient = getApolloClient();

  let postData;

  try {
    postData = await apolloClient.query({
      query: QUERY_POSTS_BY_AUTHOR_SLUG,
      variables: {
        slug,
      },
      // fetchPolicy: process.env.NODE_ENV === 'development' ? "no-cache" : "cache-first"
    });
  } catch (e) {
    console.log(`Failed to query post data: ${e.message}`);
    throw e;
  }

  const posts = postData?.data.posts.edges.map(({ node = {} }) => node);

  return {
    posts: Array.isArray(posts) && posts.map(mapPostData),
  };
}

/**
 * getPostsByCategoryId
 */

export async function getPostsByCategoryId(categoryId) {
  const apolloClient = getApolloClient();

  let postData;

  try {
    postData = await apolloClient.query({
      query: QUERY_POSTS_BY_CATEGORY_ID,
      variables: {
        categoryId,
      },
      // fetchPolicy: process.env.NODE_ENV === 'development' ? "no-cache" : "cache-first"
    });
  } catch (e) {
    console.log(`Failed to query post data: ${e.message}`);
    throw e;
  }

  const posts = postData?.data.posts.edges.map(({ node = {} }) => node);

  return {
    posts: Array.isArray(posts) && posts.map(mapPostData),
  };
}

/**
 * getPostsByCategoryId
 */

 export async function getPaginatePostsByCategoryId(categoryId, currentPage = 1, year = null, kw = null) {
  const apolloClient = getApolloClient();

  let postData, postDates;

  try {
    let obj = {
      query: QUERY_POSTS_BY_CATEGORY_ID,
      variables: {
        categoryId,
      }
    }
    if (year && !kw) {
      obj.query = QUERY_POSTS_CATEGORY_ID_BY_YEAR
      obj.variables.year = year
    } else if (!year && kw) {
      obj.query = QUERY_POSTS_CATEGORY_ID_BY_KW
      obj.variables.kw = kw
    } else if (year && kw) {
      obj.query = QUERY_POSTS_CATEGORY_ID_BY_YEAR_KW
      obj.variables.kw = kw
      obj.variables.year = year
    }
    postData = await apolloClient.query({
      ...obj,
    });

    postDates = await apolloClient.query({
      query: QUERY_POSTS_YEARS_BY_CATEGORY_ID,
      variables: {
        categoryId,
      },
    });
  } catch (e) {
    console.log(`Failed to query post data: ${e.message}`);
    throw e;
  }

  let posts = postData?.data?.posts?.edges?.map(({ node = {} }) => node);
  postDates = postDates?.data?.posts?.edges?.map(({ node = {} }) => node);
  let years = postDates.length > 0 ? _uniq(postDates.map(p => moment(p.date).year())) : []
  const postsPerPage = await getPostsPerPage();
  const pagesCount = await getPagesCount(posts, postsPerPage);
  let page = Number(currentPage);
  if (typeof page === 'undefined' || isNaN(page) || page > pagesCount) {
    page = 1;
  }
  if (posts.length < postsPerPage) page = 1
  const offset = postsPerPage * (page - 1);
  const sortedPosts = sortStickyPosts(posts);
  const postDatas = Array.isArray(sortedPosts) && sortedPosts.map(mapPostData)
  return {
    rawPosts: postDatas,
    posts: postDatas.slice(offset, offset + postsPerPage),
    years,
    pagination: {
      offset,
      postsPerPage,
      currentPage: page,
      pagesCount,
    },
  };
}

export async function getPaginatePostsByCategorySlug(categorySlug, currentPage = 1, locale) {
  const datas = await getAllPostsByCategorySlug(categorySlug, locale)
  const years = await getPostsYearByCategorySlug(categorySlug, locale)
  const postsPerPage = await getPostsPerPage();
  const pagesCount = await getPagesCount(datas, postsPerPage);
  let page = Number(currentPage);
  if (typeof page === 'undefined' || isNaN(page) || page > pagesCount) {
    page = 1;
  }
  if (datas.length < postsPerPage) page = 1
  const offset = postsPerPage * (page - 1);
  const sortedPosts = sortStickyPosts(datas);
  const postDatas = Array.isArray(sortedPosts) && sortedPosts.map(mapPostData)
  return {
    postsPerPage,
    years,
    posts: postDatas.slice(offset, offset + postsPerPage),
    rawPosts: postDatas,
    pagination: {
      offset,
      postsPerPage,
      currentPage: page,
      pagesCount,
    },
  };
}

export async function getPaginateCustomPostType(datas, currentPage = 1) {
  const postsPerPage = await getPostsPerPage();
  const pagesCount = await getPagesCount(datas, postsPerPage);
  let page = Number(currentPage);
  if (typeof page === 'undefined' || isNaN(page) || page > pagesCount) {
    page = 1;
  }
  if (datas.length < postsPerPage) page = 1
  const offset = postsPerPage * (page - 1);
  const sortedPosts = sortStickyPosts(datas);
  const postDatas = Array.isArray(sortedPosts) && sortedPosts.map(mapPostData)
  return {
    postsPerPage,
    posts: postDatas.slice(offset, offset + postsPerPage),
    rawPosts: postDatas,
    pagination: {
      offset,
      postsPerPage,
      currentPage: page,
      pagesCount,
    },
  };
}

export async function getPaginateCustomPostTypeClient(datas, postsPerPage, currentPage = 1) {
  const pagesCount = await getPagesCount(datas, postsPerPage);
  let page = Number(currentPage);
  if (typeof page === 'undefined' || isNaN(page) || page > pagesCount) {
    page = 1;
  }
  if (datas.length < postsPerPage) page = 1
  const offset = postsPerPage * (page - 1);
  const sortedPosts = sortStickyPosts(datas);
  const postDatas = Array.isArray(sortedPosts) && sortedPosts.map(mapPostData)
  return {
    postsPerPage,
    posts: postDatas.slice(offset, offset + postsPerPage),
    rawPosts: postDatas,
    pagination: {
      offset,
      postsPerPage,
      currentPage: page,
      pagesCount,
    },
  };
}

/**
 * getRecentPosts
 */

export async function getRecentPosts({ count }) {
  const { posts } = await getAllPosts();
  const sorted = sortObjectsByDate(posts);
  const datas = {
    posts: sorted.slice(0, count),
  };
  return datas
}

/**
 * sanitizeExcerpt
 */

export function sanitizeExcerpt(excerpt) {
  if (typeof excerpt !== 'string') {
    throw new Error(`Failed to sanitize excerpt: invalid type ${typeof excerpt}`);
  }

  let sanitized = excerpt;

  // If the theme includes [...] as the more indication, clean it up to just ...

  sanitized = sanitized.replace(/\s?\[&hellip;\]/, '&hellip;');

  // If after the above replacement, the ellipsis includes 4 dots, it's
  // the end of a setence

  sanitized = sanitized.replace('....', '.');
  sanitized = sanitized.replace('.&hellip;', '.');

  // If the theme is including a "Continue..." link, remove it

  sanitized = sanitized.replace(/\w*<a class="more-link".*<\/a>/, '');

  return sanitized;
}

/**
 * mapPostData
 */

export function mapPostData(post = {}) {
  const data = { ...post };

  // Clean up the author object to avoid someone having to look an extra
  // level deeper into the node

  if (data.author) {
    data.author = {
      ...data.author.node,
    };
  }

  // The URL by default that comes from Gravatar / WordPress is not a secure
  // URL. This ends up redirecting to https, but it gives mixed content warnings
  // as the HTML shows it as http. Replace the url to avoid those warnings
  // and provide a secure URL by default

  if (data.author?.avatar) {
    data.author.avatar = updateUserAvatar(data.author.avatar);
  }

  // Clean up the categories to make them more easy to access

  if (data.categories) {
    data.categories = data.categories.edges.map(({ node }) => {
      return {
        ...node,
      };
    });
  }

  // Clean up the featured image to make them more easy to access

  if (data.featuredImage) {
    data.featuredImage = data.featuredImage.node;
  }

  return data;
}

/**
 * getRelatedPosts
 */

export async function getRelatedPosts(category, postId, count = 5) {
  let relatedPosts = [];

  if (category) {
    const { posts } = await getPostsByCategoryId(category.categoryId);
    const filtered = posts.filter(({ postId: id }) => id !== postId);
    const sorted = sortObjectsByDate(filtered);
    relatedPosts = sorted.map((post) => ({ title: post.title, slug: post.slug }));
  }

  if (relatedPosts.length > count) {
    return relatedPosts.slice(0, count);
  }
  return relatedPosts;
}

/**
 * sortStickyPosts
 */

export function sortStickyPosts(posts) {
  return [...posts].sort((post) => (post.isSticky ? -1 : 1));
}

/**
 * getPostsPerPage
 */

export async function getPostsPerPage() {
  //If POST_PER_PAGE is defined at next.config.js
  if (process.env.POSTS_PER_PAGE) {
    console.warn(
      'You are using the deprecated POST_PER_PAGE variable. Use your WordPress instance instead to set this value ("Settings" > "Reading" > "Blog pages show at most").'
    );
    return Number(process.env.POSTS_PER_PAGE);
  }

  try {
    const apolloClient = getApolloClient();

    const { data } = await apolloClient.query({
      query: QUERY_POST_PER_PAGE,
      // fetchPolicy: process.env.NODE_ENV === 'development' ? "no-cache" : "cache-first"
    });

    return Number(data.allSettings.readingSettingsPostsPerPage);
  } catch (e) {
    console.log(`Failed to query post per page data: ${e.message}`);
    throw e;
  }
}

/**
 * getPageCount
 */

export async function getPagesCount(posts, postsPerPage) {
  const _postsPerPage = postsPerPage ?? (await getPostsPerPage());
  return Math.ceil(posts.length / _postsPerPage);
}

/**
 * getPaginatedPosts
 */

export async function getPaginatedPosts(currentPage = 1, locale = LANG_DEFAULT) {
  const { posts } = await getAllPosts(locale);
  const postsPerPage = await getPostsPerPage();
  const pagesCount = await getPagesCount(posts, postsPerPage);
  let page = Number(currentPage);
  if (typeof page === 'undefined' || isNaN(page) || page > pagesCount) {
    page = 1;
  }
  const offset = postsPerPage * (page - 1);
  return {
    posts: posts.slice(offset, offset + postsPerPage),
    pagination: {
      currentPage: page,
      pagesCount,
    },
  };
}

export async function getPaginatePostsByCustomPostType(posts, currentPage = 1) {
  const postsPerPage = await getPostsPerPage();
  const pagesCount = await getPagesCount(posts, postsPerPage);
  let page = Number(currentPage);
  if (typeof page === 'undefined' || isNaN(page) || page > pagesCount) {
    page = 1;
  }
  if (posts.length < postsPerPage) page = 1
  const offset = postsPerPage * (page - 1);
  const sortedPosts = sortStickyPosts(posts);
  const postDatas = Array.isArray(sortedPosts) && sortedPosts.map(mapPostData)
  return {
    posts: postDatas.slice(offset, offset + postsPerPage),
    pagination: {
      currentPage: page,
      pagesCount,
    },
  };
}
